import React, { useEffect, useState } from 'react';
import { ChevronRight, Loader2, Search, Users2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { CUSTOMER_TYPE_LABELS, getAdminUsers, ROLE_LABELS } from '../../config';

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({
    q: '',
    role: '',
    isActive: '',
    customerType: '',
  });

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const data = await getAdminUsers({
          page: String(page),
          limit: '15',
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.role ? { role: filters.role } : {}),
          ...(filters.isActive ? { is_active: filters.isActive } : {}),
          ...(filters.customerType ? { customer_type: filters.customerType } : {}),
        });
        if (!active) return;
        setUsers(data.users || []);
        setPages(data.pages || 1);
      } catch {
        if (active) setUsers([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [filters, page]);

  const updateFilter = (name, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [name]: value }));
  };

  return (
    <AdminLayout title="Utilisateurs">
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_repeat(3,1fr)] gap-3">
          <label className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={filters.q}
              onChange={(event) => updateFilter('q', event.target.value)}
              placeholder="Prenom, nom, email, telephone, raison sociale, SIRET"
              className="w-full bg-white border border-slate-200 rounded-full pl-11 pr-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
            />
          </label>

          <select
            value={filters.role}
            onChange={(event) => updateFilter('role', event.target.value)}
            className="bg-white border border-slate-200 rounded-full px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
          >
            <option value="">Tous les roles</option>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={filters.isActive}
            onChange={(event) => updateFilter('isActive', event.target.value)}
            className="bg-white border border-slate-200 rounded-full px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
          >
            <option value="">Tous les statuts</option>
            <option value="true">Actifs</option>
            <option value="false">Desactives</option>
          </select>

          <select
            value={filters.customerType}
            onChange={(event) => updateFilter('customerType', event.target.value)}
            className="bg-white border border-slate-200 rounded-full px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
          >
            <option value="">Tous les types</option>
            {Object.entries(CUSTOMER_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-secondary animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-sm border border-slate-200 p-10 text-center">
            <Users2 className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <p className="text-slate-600">Aucun utilisateur ne correspond aux filtres.</p>
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Utilisateur</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Type</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Role</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Statut</th>
                    <th className="text-left px-4 py-3 text-xs text-slate-600 font-medium">Activite</th>
                    <th className="text-right px-4 py-3 text-xs text-slate-600 font-medium">Fiche</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-200 last:border-0">
                      <td className="px-4 py-3">
                        <p className="text-primary font-medium">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                        <p className="text-xs text-slate-500">{user.phone || '-'}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {CUSTOMER_TYPE_LABELS[user.customer_type] || user.customer_type}
                        {user.company_name && <div className="text-xs text-slate-500 mt-1">{user.company_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{ROLE_LABELS[user.role] || user.role}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${user.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                          {user.is_active ? 'Actif' : 'Desactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="text-xs text-slate-600">Derniere activite: {formatDate(user.last_activity_at)}</div>
                        <div className="text-xs text-slate-500">Reservation: {formatDate(user.last_reservation_at)}</div>
                        <div className="text-xs text-slate-500">Commande: {formatDate(user.last_order_at)}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/my/utilisateurs/${user.id}`}
                          className="inline-flex items-center gap-1.5 text-secondary hover:text-secondary transition-colors"
                        >
                          Voir
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: pages }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index + 1)}
                className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                  page === index + 1
                    ? 'bg-secondary text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-100 hover:text-white'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
